import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Plus, Trash2, Edit, Download, FileText, Eye, X, GripVertical, FileSpreadsheet, Calendar, Check, Bell, Settings, ChevronDown } from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { openAuthenticatedPdfPost } from "../../utils/pdf";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { ScrollArea } from "../../components/ui/scroll-area";
import { Separator } from "../../components/ui/separator";
import { useLang } from "../../context/LangContext";
import { useCompany } from "../../context/CompanyContext";
import { formatMoney, currencySymbol } from "../../utils/currency";
import { calculateBasePrice, calculateFinalPrice, getWeekNumber, getLocalizedName, getLocalizedDesc, API, BACKEND_URL } from "../../utils/format";

const SortableProductRow = ({ prod, activeTypes, visibleColumns, lang, search, company }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: prod.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const desc = getLocalizedDesc(prod, lang);

  return (
    <tr ref={setNodeRef} style={style} className="border-b border-stone-200 hover:bg-stone-50 transition-colors" data-testid={`product-row-${prod.code}`}>
      {visibleColumns.drag && (
        <td className="p-2 w-8 cursor-grab text-stone-400 hover:text-[#8A1C2A]" {...attributes} {...listeners} data-testid={`drag-handle-${prod.code}`}>
          <GripVertical className="w-4 h-4" />
        </td>
      )}
      {visibleColumns.code && <td className="p-2 text-xs font-mono text-stone-500 align-top">{prod.code}</td>}
      {visibleColumns.description && (
        <td className="p-2 text-sm text-stone-900 align-top">
          <div className="whitespace-normal break-words leading-snug">{desc}</div>
          {prod.note && <div className="text-xs text-[#8A1C2A] mt-1 italic">★ {prod.note}</div>}
          <div className="text-[10px] text-stone-400 mt-0.5">📦 {prod.unit || 'Kg'}</div>
        </td>
      )}
      {visibleColumns.acq && <td className="p-2 text-sm text-right text-stone-700">{prod.acq?.toFixed(2)}</td>}
      {visibleColumns.marg && <td className="p-2 text-sm text-right text-stone-500">{(prod.marg * 100).toFixed(0)}%</td>}
      {visibleColumns.basePrice && (
        <td className="p-2 text-sm text-right font-semibold text-stone-800 bg-stone-50">
          {prod.base_price > 0 ? formatMoney(prod.base_price, company) : "—"}
        </td>
      )}
      {activeTypes.map(type => {
        if (!visibleColumns[`type_${type.id}`]) return null;
        const price = prod.prices?.[type.id] || 0;
        const priceNoDisc = prod.prices_no_discount?.[type.id] || price;
        const commOverrides = prod.commission_overrides || {};
        const discOverrides = prod.discount_overrides || {};
        const priceOverrides = prod.price_overrides || {};
        const hasCommOverride = commOverrides[type.id] !== undefined && commOverrides[type.id] !== null;
        const hasDiscOverride = discOverrides[type.id] !== undefined && discOverrides[type.id] !== null;
        const hasPriceOverride = priceOverrides[type.id] !== undefined && priceOverrides[type.id] !== null;
        const effDiscount = hasDiscOverride ? discOverrides[type.id] : (type.discount || 0);
        const showStrike = !hasPriceOverride && effDiscount > 0 && priceNoDisc > price;
        const cellBg = hasPriceOverride ? 'bg-stone-100' : ((hasCommOverride || hasDiscOverride) ? 'bg-[#C5A059]/25' : 'bg-[#C5A059]/10');
        const tooltipParts = [];
        if (hasPriceOverride) tooltipParts.push(`Prezzo fissato manualmente`);
        if (hasCommOverride) tooltipParts.push(`Provvigione: ${(commOverrides[type.id] * 100).toFixed(1)}%`);
        if (hasDiscOverride) tooltipParts.push(`Sconto: ${(discOverrides[type.id] * 100).toFixed(1)}%`);
        return (
          <td key={type.id} className={`p-2 text-right ${cellBg} align-middle`} title={tooltipParts.join(' · ')}>
            <div className="flex flex-col items-end justify-center gap-0.5">
              <div className="flex items-center justify-end gap-1">
                {hasPriceOverride && <span className="text-[10px] text-stone-600" title="Prezzo fissato">🔒</span>}
                {!hasPriceOverride && (hasCommOverride || hasDiscOverride) && <span className="text-[10px] text-[#C5A059] font-bold">✱</span>}
                {showStrike && <span className="text-[10px] text-stone-400 line-through">{priceNoDisc.toFixed(2)}{currencySymbol(company)}</span>}
                <span className="text-sm font-semibold text-[#8A1C2A]">{price > 0 ? formatMoney(price, company) : "—"}</span>
              </div>
              {showStrike && (
                <span className="text-[10px] text-[#C5A059] font-bold leading-none">−{(effDiscount * 100).toFixed(0)}%</span>
              )}
            </div>
          </td>
        );
      })}
      {visibleColumns.availability && (
        <td className="p-2 text-center align-middle">
          {(() => {
            // Recent price change indicator (last 7 days)
            const lastChange = prod.last_price_change_at;
            let isRecent = false;
            let daysAgo = null;
            if (lastChange) {
              const dt = new Date(lastChange);
              const diffMs = Date.now() - dt.getTime();
              const diffDays = diffMs / (1000 * 60 * 60 * 24);
              isRecent = diffDays >= 0 && diffDays <= 7;
              daysAgo = Math.floor(diffDays);
            }
            const tooltip = isRecent
              ? `Prezzo modificato ${daysAgo === 0 ? 'oggi' : daysAgo === 1 ? 'ieri' : daysAgo + ' giorni fa'}`
              : '';
            return (
              <div className="flex items-center justify-center gap-1">
                {prod.availability === "DISPONIBILE" ? (
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-100 text-green-700"><Check className="w-3 h-3" /></span>
                ) : (
                  <span className="text-stone-400 text-xs">{prod.availability?.substring(0, 8) || "—"}</span>
                )}
                {isRecent && (
                  <span
                    className="inline-flex items-center justify-center text-amber-600 animate-pulse"
                    title={tooltip}
                    data-testid={`price-alert-${prod.code}`}
                  >
                    <Bell className="w-4 h-4" />
                  </span>
                )}
              </div>
            );
          })()}
        </td>
      )}
    </tr>
  );
};

// ============ LISTINO GENERATOR ============
const ListinoGenerator = () => {
  const { t, lang } = useLang();
  const { company } = useCompany();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weekNumber, setWeekNumber] = useState(getWeekNumber());
  const [year, setYear] = useState(new Date().getFullYear());
  const [generating, setGenerating] = useState({});
  const [publishing, setPublishing] = useState({});
  const [publishConfirm, setPublishConfirm] = useState(null);
  const [historyView, setHistoryView] = useState(null);
  const [previewPdf, setPreviewPdf] = useState(null);
  const [primaryLang, setPrimaryLang] = useState(localStorage.getItem('pdf_primary_lang') || 'it');
  const switchPrimaryLang = (l) => { localStorage.setItem('pdf_primary_lang', l); setPrimaryLang(l); };
  const [search, setSearch] = useState("");
  const [productsByCat, setProductsByCat] = useState({});
  const [visibleColumns, setVisibleColumns] = useState({
    drag: true, code: true, description: true, acq: true, marg: false, basePrice: true, availability: true,
  });
  const [colsMenuOpen, setColsMenuOpen] = useState(false);
  const [columnFilters, setColumnFilters] = useState({
    code: "", description: "", acq: "", marg: "", basePrice: "", availability: "",
  });
  const [typeFilters, setTypeFilters] = useState({});

  const setFilter = (key, val) => setColumnFilters(p => ({ ...p, [key]: val }));
  const setTypeFilter = (id, val) => setTypeFilters(p => ({ ...p, [id]: val }));
  const clearAllFilters = () => {
    setColumnFilters({ code: "", description: "", acq: "", marg: "", basePrice: "", availability: "" });
    setTypeFilters({});
    setSearch("");
  };
  const hasFilters = search || Object.values(columnFilters).some(v => v) || Object.values(typeFilters).some(v => v);

  // helper: substring contains. Numbers are matched as substrings of formatted value too.
  const matches = (val, q) => {
    if (!q) return true;
    const s = String(val ?? "").toLowerCase();
    return s.includes(q.toLowerCase());
  };

  const fetchData = async () => {
    try {
      const response = await axios.get(`${API}/listino-data`);
      setData(response.data);
      setProductsByCat(response.data.products_by_category || {});
      setWeekNumber(response.data.week_number);
      setYear(response.data.year);
      // Make all type columns visible by default
      const newCols = { ...visibleColumns };
      (response.data.pricelist_types || []).forEach(t => { newCols[`type_${t.id}`] = true; });
      setVisibleColumns(newCols);
    } catch (error) {
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event, catId) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const catProducts = productsByCat[catId] || [];
    const oldIndex = catProducts.findIndex(p => p.id === active.id);
    const newIndex = catProducts.findIndex(p => p.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const newOrder = arrayMove(catProducts, oldIndex, newIndex);
    setProductsByCat(prev => ({ ...prev, [catId]: newOrder }));

    // Persist order
    const orders = {};
    newOrder.forEach((p, idx) => { orders[p.id] = idx; });
    try {
      await axios.post(`${API}/products/reorder`, { orders });
      toast.success("Ordine aggiornato");
    } catch {
      toast.error("Errore aggiornamento ordine");
      fetchData();
    }
  };

  const generatePDFForType = async (type, withCommission = false) => {
    const key = withCommission ? `comm_${type.id}` : type.id;
    setGenerating(prev => ({ ...prev, [key]: true }));
    try {
      const langPrefix = primaryLang === 'en' ? 'EN' : 'IT';
      const safeName = type.name.replace(/\s+/g, '_').replace(/\//g, '-').replace(/%/g, 'pct');
      const ok = await openAuthenticatedPdfPost(`/generate-pdf`, {
        pricelist_type_id: type.id,
        week_number: weekNumber,
        year: year,
        primary_lang: primaryLang,
        show_commission: withCommission,
      }, { download: true, filename: `${langPrefix}_${safeName}_W${weekNumber}_${year}${withCommission ? '_PROVVIGIONI' : ''}.pdf` });
      if (ok) toast.success(withCommission ? `PDF interno "${type.name}" con provvigioni generato` : `PDF "${type.name}" generato!`);
    } finally {
      setGenerating(prev => ({ ...prev, [key]: false }));
    }
  };

  const generateExcelForType = async (type) => {
    setGenerating(prev => ({ ...prev, [`xls_${type.id}`]: true }));
    try {
      const response = await axios.post(`${API}/generate-excel`, {
        pricelist_type_id: type.id,
        week_number: weekNumber,
        year: year,
        primary_lang: primaryLang,
      }, { responseType: 'blob' });
      const langPrefix = primaryLang === 'en' ? 'EN' : 'IT';
      const safeName = type.name.replace(/\s+/g, '_').replace(/\//g, '-').replace(/%/g, 'pct');
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${langPrefix}_${safeName}_W${weekNumber}_${year}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Excel "${type.name}" generato!`);
    } catch {
      toast.error("Errore generazione Excel");
    } finally {
      setGenerating(prev => ({ ...prev, [`xls_${type.id}`]: false }));
    }
  };

  const previewPDFForType = async (type) => {
    setGenerating(prev => ({ ...prev, [`prev_${type.id}`]: true }));
    try {
      const response = await axios.post(`${API}/generate-pdf`, {
        pricelist_type_id: type.id,
        week_number: weekNumber,
        year: year,
        primary_lang: primaryLang,
      }, { responseType: 'blob' });

      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const langPrefix = primaryLang === 'en' ? 'EN' : 'IT';
      const safeName = type.name.replace(/\s+/g, '_').replace(/\//g, '-').replace(/%/g, 'pct');
      setPreviewPdf({
        url: blobUrl,
        name: type.name,
        type,
        filename: `${langPrefix}_${safeName}_W${weekNumber}_${year}.pdf`,
      });
    } catch (error) {
      toast.error("Errore nell'anteprima");
    } finally {
      setGenerating(prev => ({ ...prev, [`prev_${type.id}`]: false }));
    }
  };

  const publishPricelist = async (type) => {
    setPublishing(prev => ({ ...prev, [type.id]: true }));
    try {
      await axios.post(`${API}/pricelist-types/${type.id}/publish`);
      toast.success(`Listino "${type.name}" pubblicato come ufficiale!`);
      setPublishConfirm(null);
      fetchData();
    } catch (error) {
      toast.error("Errore nella pubblicazione");
    } finally {
      setPublishing(prev => ({ ...prev, [type.id]: false }));
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner w-8 h-8" /></div>;
  }

  const categories = data?.categories || [];
  const pricelistTypes = data?.pricelist_types || [];

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div>
          <h2 className="text-2xl font-display font-semibold text-stone-900 flex items-center gap-2">
            <FileSpreadsheet className="w-6 h-6 text-[#8A1C2A]" />
            {t.generator}
          </h2>
          <p className="text-stone-500 text-sm mt-1">Visualizza prezzi e scarica PDF separati per ogni tipo di listino</p>
        </div>
      </div>

      {/* Settings Bar */}
      <Card className="bg-white border-stone-200 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-6 items-center">
            <div className="flex items-center gap-3">
              <Label className="text-stone-600 text-sm">{t.week}:</Label>
              <Input type="number" value={weekNumber} onChange={(e) => setWeekNumber(parseInt(e.target.value) || 1)} className="w-20 h-9 bg-white border-stone-300 text-stone-900 text-center" data-testid="week-input" />
              <Label className="text-stone-600 text-sm">{t.year}:</Label>
              <Input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value) || 2026)} className="w-24 h-9 bg-white border-stone-300 text-stone-900 text-center" data-testid="year-input" />
            </div>

            <Separator orientation="vertical" className="h-8 bg-stone-200" />

            {/* Column visibility toggler */}
            <div className="relative">
              <Button variant="outline" size="sm" onClick={() => setColsMenuOpen(!colsMenuOpen)} className="border-stone-300 text-stone-700 hover:bg-stone-50" data-testid="toggle-columns-btn">
                <Settings className="w-4 h-4 mr-2" />{t.columns}
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
              {colsMenuOpen && (
                <div className="absolute top-full mt-2 z-30 bg-white border border-stone-200 rounded-md shadow-lg p-3 w-56 space-y-2">
                  {[
                    { k: 'drag', l: 'Drag handle' },
                    { k: 'code', l: t.code },
                    { k: 'description', l: t.description },
                    { k: 'acq', l: 'ACQ €' },
                    { k: 'marg', l: 'Marg %' },
                    { k: 'basePrice', l: t.basePrice },
                    { k: 'availability', l: t.availability },
                  ].map(c => (
                    <label key={c.k} className="flex items-center gap-2 cursor-pointer text-sm text-stone-700">
                      <Checkbox checked={visibleColumns[c.k]} onCheckedChange={() => setVisibleColumns(prev => ({ ...prev, [c.k]: !prev[c.k] }))} data-testid={`col-toggle-${c.k}`} />
                      {c.l}
                    </label>
                  ))}
                  <Separator />
                  <p className="text-xs font-semibold text-stone-500 uppercase">Listini</p>
                  {pricelistTypes.map(type => (
                    <label key={type.id} className="flex items-center gap-2 cursor-pointer text-sm text-stone-700">
                      <Checkbox checked={!!visibleColumns[`type_${type.id}`]} onCheckedChange={() => setVisibleColumns(prev => ({ ...prev, [`type_${type.id}`]: !prev[`type_${type.id}`] }))} data-testid={`col-toggle-type-${type.id}`} />
                      {type.name}
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* PDF Download + Publish Buttons - One panel per pricelist type */}
      <Card className="bg-gradient-to-r from-[#8A1C2A]/5 to-[#C5A059]/5 border-[#C5A059]/30 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="text-sm font-semibold text-stone-800 flex items-center gap-2">
              <Download className="w-4 h-4 text-[#8A1C2A]" />
              Listini settimanali
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-stone-600 font-medium">Lingua principale PDF:</span>
              <div className="inline-flex rounded-md border border-stone-300 bg-white overflow-hidden" data-testid="primary-lang-switcher">
                <button
                  onClick={() => switchPrimaryLang('it')}
                  className={`px-3 py-1 text-xs font-semibold transition-colors ${primaryLang === 'it' ? 'bg-[#8A1C2A] text-white' : 'text-stone-700 hover:bg-stone-50'}`}
                  data-testid="primary-lang-it"
                >
                  🇮🇹 IT
                </button>
                <button
                  onClick={() => switchPrimaryLang('en')}
                  className={`px-3 py-1 text-xs font-semibold transition-colors border-l border-stone-300 ${primaryLang === 'en' ? 'bg-[#8A1C2A] text-white' : 'text-stone-700 hover:bg-stone-50'}`}
                  data-testid="primary-lang-en"
                >
                  🇬🇧 EN
                </button>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {pricelistTypes.map(type => (
              <div key={type.id} className="bg-white border border-stone-200 rounded-lg p-3 space-y-2 hover:border-[#C5A059] transition-all" data-testid={`pricelist-card-${type.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-stone-900 truncate">{type.name}</p>
                    {type.discount > 0 && (
                      <Badge className="bg-[#C5A059] text-white text-[10px] mt-0.5">🏷️ −{(type.discount * 100).toFixed(1)}%</Badge>
                    )}
                  </div>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  <Button
                    onClick={() => previewPDFForType(type)}
                    disabled={generating[`prev_${type.id}`]}
                    variant="outline"
                    className="flex-1 border-stone-300 text-stone-700 hover:bg-stone-50 h-8 text-xs"
                    size="sm"
                    data-testid={`preview-pdf-${type.id}`}
                    title="Apri anteprima PDF"
                  >
                    <Eye className="w-3 h-3 mr-1" />
                    {generating[`prev_${type.id}`] ? "..." : "Anteprima"}
                  </Button>
                  <Button
                    onClick={() => generatePDFForType(type)}
                    disabled={generating[type.id]}
                    className="flex-1 bg-[#8A1C2A] hover:bg-[#6B1420] text-white h-8 text-xs"
                    size="sm"
                    data-testid={`download-pdf-${type.id}`}
                  >
                    <Download className="w-3 h-3 mr-1" />
                    {generating[type.id] ? "..." : "PDF"}
                  </Button>
                  <Button
                    onClick={() => generateExcelForType(type)}
                    disabled={generating[`xls_${type.id}`]}
                    className="flex-1 bg-green-700 hover:bg-green-800 text-white h-8 text-xs"
                    size="sm"
                    data-testid={`download-excel-${type.id}`}
                    title="Scarica listino in formato Excel"
                  >
                    <FileSpreadsheet className="w-3 h-3 mr-1" />
                    {generating[`xls_${type.id}`] ? "..." : "Excel"}
                  </Button>
                  <Button
                    onClick={() => generatePDFForType(type, true)}
                    disabled={generating[`comm_${type.id}`]}
                    variant="outline"
                    className="w-full border-[#1F4E79] text-[#1F4E79] hover:bg-[#1F4E79]/10 h-8 text-xs"
                    size="sm"
                    data-testid={`download-pdf-commission-${type.id}`}
                    title="Versione interna: per ogni articolo mostra la provvigione agente (% e importo per unità). Non inviarla ai clienti."
                  >
                    <Download className="w-3 h-3 mr-1" />
                    {generating[`comm_${type.id}`] ? "..." : "PDF con provvigioni (interno)"}
                  </Button>
                </div>
                <div className="flex gap-1.5">
                  <Button
                    onClick={() => setPublishConfirm(type)}
                    disabled={publishing[type.id]}
                    variant="outline"
                    className="flex-1 border-[#C5A059] text-[#8A1C2A] hover:bg-[#C5A059]/10 h-8 text-xs"
                    size="sm"
                    data-testid={`publish-${type.id}`}
                    title="Pubblica questo listino come base ufficiale"
                  >
                    📌 Pubblica
                  </Button>
                  {type.publish_history && type.publish_history.length > 0 && (
                    <Button
                      onClick={() => setHistoryView(type)}
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-stone-500 hover:text-[#8A1C2A]"
                      data-testid={`history-${type.id}`}
                      title="Cronologia pubblicazioni"
                    >
                      <FileText className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
                {type.last_published_at && (
                  <p className="text-[10px] text-stone-500">
                    📌 {new Date(type.last_published_at).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}
                  </p>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Search + Clear Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input placeholder={t.search} value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 bg-white border-stone-300 text-stone-900" data-testid="generator-search" />
        </div>
        {hasFilters && (
          <Button variant="outline" size="sm" onClick={clearAllFilters} className="border-stone-300 text-stone-600 hover:bg-stone-50" data-testid="clear-filters-btn">
            <X className="w-4 h-4 mr-1" />Cancella filtri
          </Button>
        )}
      </div>

      {/* Spreadsheet Table */}
      <Card className="bg-white border-stone-200 shadow-sm">
        <div className="max-h-[calc(100vh-460px)] overflow-auto" style={{ WebkitOverflowScrolling: "touch" }}>
          <table className="w-full border-collapse" style={{ tableLayout: 'auto' }}>
            <thead className="sticky top-0 z-20">
              <tr className="bg-[#8A1C2A]">
                {visibleColumns.drag && <th className="w-8 p-2"></th>}
                {visibleColumns.code && <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-white min-w-[120px]" style={{ resize: 'horizontal', overflow: 'hidden' }}>{t.code}</th>}
                {visibleColumns.description && <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-white min-w-[280px]" style={{ resize: 'horizontal', overflow: 'hidden' }}>{t.description}</th>}
                {visibleColumns.acq && <th className="text-right p-3 text-xs font-semibold uppercase tracking-wider text-white min-w-[80px]" style={{ resize: 'horizontal', overflow: 'hidden' }}>ACQ €</th>}
                {visibleColumns.marg && <th className="text-right p-3 text-xs font-semibold uppercase tracking-wider text-white min-w-[70px]" style={{ resize: 'horizontal', overflow: 'hidden' }}>Marg%</th>}
                {visibleColumns.basePrice && <th className="text-right p-3 text-xs font-semibold uppercase tracking-wider text-white bg-stone-700 min-w-[110px]" style={{ resize: 'horizontal', overflow: 'hidden' }}>{t.basePrice}</th>}
                {pricelistTypes.map(type => (
                  visibleColumns[`type_${type.id}`] && (
                    <th key={type.id} className="text-right p-3 text-xs font-semibold uppercase tracking-wider text-white bg-[#C5A059] min-w-[110px]" style={{ resize: 'horizontal', overflow: 'hidden' }}>
                      {type.name}
                    </th>
                  )
                ))}
                {visibleColumns.availability && <th className="text-center p-3 text-xs font-semibold uppercase tracking-wider text-white w-24">{t.availability} / 🔔</th>}
              </tr>
              {/* Filter row */}
              <tr className="bg-stone-50 border-b-2 border-[#8A1C2A]">
                {visibleColumns.drag && <th className="p-1"></th>}
                {visibleColumns.code && (
                  <th className="p-1">
                    <Input value={columnFilters.code} onChange={(e) => setFilter('code', e.target.value)} placeholder="🔍 Codice" className="h-7 text-xs bg-white border-stone-300 placeholder:text-stone-400" data-testid="filter-code" />
                  </th>
                )}
                {visibleColumns.description && (
                  <th className="p-1">
                    <Input value={columnFilters.description} onChange={(e) => setFilter('description', e.target.value)} placeholder="🔍 Descrizione, nota, EN" className="h-7 text-xs bg-white border-stone-300 placeholder:text-stone-400" data-testid="filter-description" />
                  </th>
                )}
                {visibleColumns.acq && (
                  <th className="p-1">
                    <Input value={columnFilters.acq} onChange={(e) => setFilter('acq', e.target.value)} placeholder="🔍 ACQ" className="h-7 text-xs bg-white border-stone-300 placeholder:text-stone-400 text-right" data-testid="filter-acq" />
                  </th>
                )}
                {visibleColumns.marg && (
                  <th className="p-1">
                    <Input value={columnFilters.marg} onChange={(e) => setFilter('marg', e.target.value)} placeholder="🔍" className="h-7 text-xs bg-white border-stone-300 placeholder:text-stone-400 text-right" data-testid="filter-marg" />
                  </th>
                )}
                {visibleColumns.basePrice && (
                  <th className="p-1">
                    <Input value={columnFilters.basePrice} onChange={(e) => setFilter('basePrice', e.target.value)} placeholder="🔍" className="h-7 text-xs bg-white border-stone-300 placeholder:text-stone-400 text-right" data-testid="filter-base-price" />
                  </th>
                )}
                {pricelistTypes.map(type => (
                  visibleColumns[`type_${type.id}`] && (
                    <th key={type.id} className="p-1">
                      <Input value={typeFilters[type.id] || ""} onChange={(e) => setTypeFilter(type.id, e.target.value)} placeholder="🔍" className="h-7 text-xs bg-white border-stone-300 placeholder:text-stone-400 text-right" data-testid={`filter-type-${type.id}`} />
                    </th>
                  )
                ))}
                {visibleColumns.availability && (
                  <th className="p-1">
                    <Input value={columnFilters.availability} onChange={(e) => setFilter('availability', e.target.value)} placeholder="🔍" className="h-7 text-xs bg-white border-stone-300 placeholder:text-stone-400" data-testid="filter-availability" />
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {categories.map(cat => {
                const catProducts = (productsByCat[cat.id] || []).filter(p => {
                  // Global search
                  if (search) {
                    const q = search.toLowerCase();
                    const inGlobal = p.code?.toLowerCase().includes(q) ||
                      p.description?.toLowerCase().includes(q) ||
                      p.description_en?.toLowerCase().includes(q);
                    if (!inGlobal) return false;
                  }
                  // Per-column filters
                  if (!matches(p.code, columnFilters.code)) return false;
                  const descCombined = `${p.description || ''} ${p.description_en || ''} ${p.note || ''}`;
                  if (!matches(descCombined, columnFilters.description)) return false;
                  if (columnFilters.acq && !matches(p.acq?.toFixed(2), columnFilters.acq)) return false;
                  if (columnFilters.marg && !matches((p.marg * 100).toFixed(0), columnFilters.marg)) return false;
                  if (columnFilters.basePrice && !matches(p.base_price?.toFixed(2), columnFilters.basePrice)) return false;
                  if (columnFilters.availability) {
                    if (!matches(p.availability, columnFilters.availability)) return false;
                  }
                  // Per pricelist-type filters
                  for (const pt of pricelistTypes) {
                    const f = typeFilters[pt.id];
                    if (f && !matches(p.prices?.[pt.id]?.toFixed(2), f)) return false;
                  }
                  return true;
                });
                if (catProducts.length === 0) return null;

                const catName = getLocalizedName(cat, lang);
                const colCount = Object.values(visibleColumns).filter(Boolean).length + pricelistTypes.filter(t => visibleColumns[`type_${t.id}`]).length;

                return (
                  <React.Fragment key={cat.id}>
                    <tr className="bg-gradient-to-r from-[#C5A059] to-[#B08D4A]">
                      <td colSpan={colCount} className="p-2 text-sm font-bold text-white uppercase tracking-wide">
                        {catName}
                      </td>
                    </tr>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} accessibility={{ container: document.body }} onDragEnd={(e) => handleDragEnd(e, cat.id)}>
                      <SortableContext items={catProducts.map(p => p.id)} strategy={verticalListSortingStrategy}>
                        {catProducts.map(prod => (
                          <SortableProductRow
                            key={prod.id}
                            prod={prod}
                            activeTypes={pricelistTypes}
                            visibleColumns={visibleColumns}
                            lang={lang}
                            search={search}
                            company={company}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="flex items-center gap-4 text-xs text-stone-500">
        <span>💡 Trascina ⋮⋮ per riordinare · 🔍 Filtra per colonna · ↔️ Ridimensiona colonne dal bordo destro dell'header</span>
      </div>

      {/* PDF Preview dialog */}
      <Dialog open={!!previewPdf} onOpenChange={(open) => {
        if (!open && previewPdf) {
          window.URL.revokeObjectURL(previewPdf.url);
          setPreviewPdf(null);
        }
      }}>
        <DialogContent className="bg-white border-stone-200 max-w-5xl w-[90vw] h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-2 border-b border-stone-200">
            <DialogTitle className="text-stone-900 flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#8A1C2A]" />
              Anteprima: {previewPdf?.name}
            </DialogTitle>
          </DialogHeader>
          {previewPdf && (
            <object
              data={previewPdf.url}
              type="application/pdf"
              className="flex-1 w-full"
              data-testid="preview-iframe"
            >
              <div className="flex flex-col items-center justify-center h-full text-center p-8 space-y-4">
                <p className="text-stone-600">L'anteprima inline non è disponibile nel tuo browser.</p>
                <Button
                  onClick={() => window.open(previewPdf.url, '_blank')}
                  className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white"
                >
                  <Eye className="w-4 h-4 mr-2" />Apri in nuova scheda
                </Button>
              </div>
            </object>
          )}
          <DialogFooter className="px-6 pb-6 pt-2 border-t border-stone-200 gap-2">
            <Button variant="outline" onClick={() => {
              if (previewPdf) window.URL.revokeObjectURL(previewPdf.url);
              setPreviewPdf(null);
            }} className="border-stone-300">Chiudi</Button>
            <Button
              variant="outline"
              onClick={() => previewPdf && window.open(previewPdf.url, '_blank')}
              className="border-[#C5A059] text-[#8A1C2A] hover:bg-[#C5A059]/10"
              data-testid="preview-newtab-btn"
            >
              <Eye className="w-4 h-4 mr-2" />Nuova scheda
            </Button>
            <Button
              onClick={() => {
                if (!previewPdf) return;
                const link = document.createElement('a');
                link.href = previewPdf.url;
                link.setAttribute('download', previewPdf.filename);
                document.body.appendChild(link);
                link.click();
                link.remove();
                toast.success("PDF scaricato");
              }}
              className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white"
              data-testid="preview-download-btn"
            >
              <Download className="w-4 h-4 mr-2" />Scarica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish confirmation dialog */}
      <Dialog open={!!publishConfirm} onOpenChange={(open) => !open && setPublishConfirm(null)}>
        <DialogContent className="bg-white border-stone-200">
          <DialogHeader>
            <DialogTitle className="text-stone-900 flex items-center gap-2">
              📌 Pubblica come listino ufficiale
            </DialogTitle>
          </DialogHeader>
          {publishConfirm && (
            <div className="space-y-3 text-sm text-stone-700">
              <p>Stai per pubblicare <strong className="text-[#8A1C2A]">"{publishConfirm.name}"</strong> come listino ufficiale di riferimento.</p>
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-amber-900 text-xs">
                ⚠️ <strong>Cosa succede:</strong>
                <ul className="list-disc list-inside mt-1 space-y-0.5">
                  <li>Tutti i flag <strong>✓ AGGIORNATO</strong> di questo listino verranno azzerati</li>
                  <li>I prezzi attuali diventano la nuova base di riferimento</li>
                  <li>Le future variazioni di prezzo torneranno a mostrare il flag</li>
                  <li>Le modifiche testuali (descrizioni, note, ordine) <strong>non</strong> generano flag</li>
                </ul>
              </div>
              <p className="text-stone-500 text-xs">Sicuro di voler procedere?</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPublishConfirm(null)} className="border-stone-300" data-testid="publish-cancel-btn">Annulla</Button>
            <Button
              onClick={() => publishConfirm && publishPricelist(publishConfirm)}
              disabled={publishConfirm && publishing[publishConfirm.id]}
              className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white"
              data-testid="publish-confirm-btn"
            >
              {publishConfirm && publishing[publishConfirm.id] ? "..." : "📌 Conferma e Pubblica"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Publish history dialog */}
      <Dialog open={!!historyView} onOpenChange={(open) => !open && setHistoryView(null)}>
        <DialogContent className="bg-white border-stone-200 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-stone-900 flex items-center gap-2">
              📜 Cronologia: {historyView?.name}
            </DialogTitle>
          </DialogHeader>
          {historyView && (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {(historyView.publish_history || []).length === 0 ? (
                <p className="text-sm text-stone-500 italic">Nessuna pubblicazione ancora effettuata.</p>
              ) : (
                (historyView.publish_history || []).map((h, i) => (
                  <div key={i} className={`p-3 rounded border ${i === 0 ? 'bg-[#C5A059]/10 border-[#C5A059]' : 'bg-stone-50 border-stone-200'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-stone-900">
                          {new Date(h.published_at).toLocaleString('it-IT', { dateStyle: 'long', timeStyle: 'short' })}
                        </p>
                        <p className="text-xs text-stone-500">da {h.user_name || h.user_email || "sconosciuto"}</p>
                      </div>
                      {i === 0 && <Badge className="bg-[#8A1C2A] text-white text-[10px]">ATTUALE</Badge>}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryView(null)} className="border-stone-300">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default ListinoGenerator;
export { SortableProductRow };

